/**
 * Income type enumeration (for portfolio income)
 * 
 * Represents the type of income, not how it's taxed.
 * Tax classification is handled separately via TaxCategory.
 */
export enum IncomeType {
  Dividend = 'dividend',
  ReinvestedDividend = 'reinvested_dividend',
  Interest = 'interest',
  OptionPremium = 'option_premium',
  CapitalGainDistribution = 'capital_gain_distribution', // Fund cap-gain distribution; character is in TaxCategory
}

/**
 * External income type enumeration (for non-portfolio income)
 *
 * Note: Enum values match Prisma schema. '1099' is spelled out as 'ninetyninee'
 * because enum identifiers can't start with numbers.
 */
export enum ExternalIncomeType {
  W2 = 'w2',
  Form1099Nec = 'ninetyninee_nec',    // 1099-NEC
  Form1099Misc = 'ninetyninee_misc',   // 1099-MISC
  PrivateStockSaleLong = 'private_stock_sale_long',
  PrivateStockSaleShort = 'private_stock_sale_short',
  SavingsInterest = 'savings_interest',
  Rental = 'rental',
  K1 = 'k1',
  IsoExercise = 'iso_exercise',
  Other = 'other',
}

/**
 * Validate and convert income type string
 */
export function toIncomeType(type: string | null | undefined): IncomeType | undefined {
  if (!type) return undefined;
  const normalized = type.toLowerCase().replace(/\s+/g, '_');
  return Object.values(IncomeType).find(t => t === normalized);
}

/**
 * Check if income type is taxed as capital gains (not ordinary income)
 */
export function isTaxedAsCapitalGains(type: IncomeType): boolean {
  return type === IncomeType.OptionPremium;
}
